import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import { useOutletContext } from 'react-router-dom';
import { DateObject } from 'react-multi-date-picker';
import { Popover } from '@mui/material';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useApiData } from '../../../config/reducer/Modules';
import {
  APIGetMaintenanceRecordsAgencyList,
  APIGetDevices,
  APIGetMaintenanceRecordsByAssetIdQuiet,
} from '../../../config/restAPI/FetchAPI';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import Title from '../../../component/title/Title';
import SearchInput from '../../../component/searchInput/SearchInput';
import DateRangePicker from '../../../component/dateRangePicker/DateRangePicker';
import MenuOptionItem from '../../../component/menuOptionItem/MenuOptionItem';
import Checkbox from '../../../component/checkbox/Checkbox';
import FilterIcon from '../../../component/filterButton/img/filter-lines.svg';
import VerticalDots from '../../../component/table/img/verticalDots.svg';
import Table from '../../../component/table/Table';
import SnackBar from '../../../component/snackbar/Snackbar';
import '../../../component/menuPopup/menu-popup.css';
import '../../../component/checkbox/Checkbox.css';
import '../AssetPerformance/AssetPerformance.css';
import './Maintenance.css';
import '../../inventory/maintenanceModule/MaintenanceModule.css';

function maintenanceNormalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

/** Human-readable asset type for table/modal (API often uses `make`, not `assetType`). */
function maintenanceFormatAssetTypeDisplay(assetIdentification = {}, record = {}) {
  const raw = assetIdentification?.assetType
    ?? assetIdentification?.make
    ?? record?.assetType;
  if (raw == null || raw === '') return '-';
  if (Array.isArray(raw)) return raw.filter(Boolean).join(', ') || '-';
  return String(raw);
}

function maintenanceAssetTypeSectionDisplay(assetIdentification = {}, record = {}) {
  const raw = assetIdentification?.assetType
    ?? assetIdentification?.make
    ?? record?.assetType;
  if (raw == null || raw === '') return '';
  if (Array.isArray(raw)) return raw.filter(Boolean).join(', ') || '';
  return String(raw);
}

/** assetType may be string, comma-separated string, or array (see MaintenanceModule). */
function maintenanceRecordAssetTypeTokens(record) {
  const raw = record?.assetIdentification?.assetType
    ?? record?.assetIdentification?.make
    ?? record?.assetType;
  if (raw == null || raw === '') return [];
  const tokens = [];
  const pushSplit = (chunk) => {
    String(chunk)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((t) => tokens.push(t.toLowerCase()));
  };
  if (Array.isArray(raw)) {
    raw.forEach((item) => pushSplit(item));
  } else {
    pushSplit(raw);
  }
  return [...new Set(tokens)];
}

/** Concatenate record fields for free-text search (matches table columns + common API fields). */
function maintenanceRecordSearchHaystack(record) {
  const ai = record?.assetIdentification || {};
  const ri = record?.receiptInfo || {};
  const v = record?.vendor || {};
  const sd = record?.serviceDetails || {};
  const parts = [];

  const push = (val) => {
    if (val == null || val === '') return;
    if (Array.isArray(val)) {
      val.forEach((item) => push(item));
      return;
    }
    parts.push(String(val));
  };

  push(ai.assetName);
  push(ai.vehicleRegNum);
  push(ai.assetRegNum);
  push(ai.assetType);
  push(ai.make);
  push(record?.assetName);
  push(record?.assetType);
  push(ri.receiptNumber);
  push(ri.invoiceNumber);
  push(ri.currency);
  push(ri.paymentMethod);
  push(ri.notes);
  push(v.name);
  push(v.address);
  push(v.email);
  push(v.contactNumber);
  push(sd.type);
  push(sd.jobDescription);
  push(record?.label);
  push(record?.id);
  push(record?.oid);
  push(record?.tags);

  const rd = ri.receiptDate ?? ri.receipt_date;
  if (rd) {
    const m = moment(rd);
    if (m.isValid()) {
      push(m.format('DD/MM/YYYY'));
      push(m.format('YYYY-MM-DD'));
      push(m.format('DD-MM-YYYY'));
    }
  }
  const sv = ri.serviceDate ?? ri.service_date;
  if (sv) {
    const m = moment(sv);
    if (m.isValid()) {
      push(m.format('DD/MM/YYYY'));
      push(m.format('YYYY-MM-DD'));
    }
  }

  return maintenanceNormalizeText(parts.join(' '));
}

function maintenanceRecordDisplayAssetName(record) {
  const ai = record?.assetIdentification || {};
  const raw = ai.assetName || ai.vehicleRegNum || record?.assetName || '';
  return String(raw).trim() || '-';
}

function maintenanceInsightsTh(label) {
  return <span className="maintenance-insights-th-text">{label}</span>;
}

const MAINTENANCE_ASSET_TYPE_FILTERS = ['Road', 'Rail', 'EV1', 'EV2', 'Hybrid'];

function MaintenanceInsightsRowMenu({
  row, rowIndex, onView, onDownload,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="maintenance-insights-row-menu">
      <button
        type="button"
        className="assets-button"
        id={`maintenance-insights-menu-${row.id}-${rowIndex}`}
        aria-label="Row actions"
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="true"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <div className="assets-menu">
          <img loading="lazy" src={VerticalDots} alt="" />
        </div>
      </button>
      <Popover
        id={`maintenance-insights-popover-${row.id}-${rowIndex}`}
        sx={{
          boxShadow: 2,
          borderRadius: 3,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <div className="menupopup-container">
          <MenuOptionItem
            label="View"
            bottomBorder
            menuButtonClicked={() => {
              handleClose();
              onView(row);
            }}
          />
          <MenuOptionItem
            label="Download"
            bottomBorder={false}
            menuButtonClicked={() => {
              handleClose();
              onDownload(row);
            }}
          />
        </div>
      </Popover>
    </div>
  );
}

const MaintenanceInsights = () => {
  const token = Cookies.get('jwtToken');
  const agencyID = Cookies.get('agencyUUID');
  const { apiResponse } = useApiData();
  const [navigtionClick, setNavigtionClick, _navigationState, setNavigationState] = useOutletContext();

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
  const [selectedAssetNames, setSelectedAssetNames] = useState([]);
  /** Service date filter (YYYY-MM-DD), applied when the date range picker Apply is used. */
  const [serviceDateFrom, setServiceDateFrom] = useState('');
  const [serviceDateTo, setServiceDateTo] = useState('');
  const [servicePickerDate, setServicePickerDate] = useState([]);
  const [servicePickerInputValue, setServicePickerInputValue] = useState('Service Dates');
  const [serviceMinDateRange, setServiceMinDateRange] = useState(new DateObject().subtract(1, 'year'));
  const [serviceMaxDateRange, setServiceMaxDateRange] = useState(new Date());
  const [serviceDateRangePreset, setServiceDateRangePreset] = useState('');
  const [applyServicePicker, setApplyServicePicker] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportSnack, setExportSnack] = useState(false);
  const [selectedReportRow, setSelectedReportRow] = useState(null);
  const reportPdfRef = useRef(null);
  const downloadPdfRef = useRef(null);
  const [downloadReportRow, setDownloadReportRow] = useState(null);

  const assetPerformanceNavigationList = [
    {
      text: 'Asset Performance',
      value: 1,
      path: '/insights/assetPerformance',
      enabled: (apiResponse?.role === 'Agency Administrator'
        || apiResponse?.role === 'Agency Owner')
        ? true
        : apiResponse?.module?.insights?.assetPerformance,
    },
    {
      text: 'O/D Analytics',
      value: 5,
      path: '/insights/odAnalytics',
      enabled: (apiResponse?.role === 'Agency Administrator'
        || apiResponse?.role === 'Agency Owner')
        ? true
        : apiResponse?.module?.insights?.odAnalytics,
    },
    {
      text: 'Maintenance',
      value: 6,
      path: '/insights/maintenance',
      enabled: true,
    },
  ];

  const parseMaintenanceRecordsFromApiResult = (result) => {
    const isSuccess = result?.data?.status === 200 || result?.status === 200;
    if (!isSuccess) return [];
    const responseData = result?.data?.data ?? result?.data;
    let res = [];
    if (Array.isArray(responseData)) {
      res = responseData;
    } else if (responseData && typeof responseData === 'object') {
      res = responseData?.records
        || responseData?.items
        || responseData?.maintenanceRecords
        || responseData?.maintenance_records
        || responseData?.results
        || [];
      if (!Array.isArray(res) || res.length === 0) {
        const hasReceiptInfo = Boolean(responseData?.receiptInfo || responseData?.serviceDetails);
        res = hasReceiptInfo ? [responseData] : [];
      }
    }
    return Array.isArray(res) ? res : [];
  };

  const fetchAllRecords = async () => {
    if (!token || !agencyID) return;
    setLoading(true);
    try {
      const listResult = await APIGetMaintenanceRecordsAgencyList(token, agencyID, {
        pageNumber: 1,
        pageSize: 5000,
      });
      let parsed = parseMaintenanceRecordsFromApiResult(listResult);

      // If the agency list API returns nothing, fall back to per-asset aggregation
      // (same behaviour as MaintenanceModule) but only then, to avoid many calls
      // when the list endpoint is working.
      if (!parsed.length) {
        const deviceResult = await APIGetDevices(() => {}, token, agencyID, {
          pageNumber: 1,
          totalPerPage: 500,
        });
        if (deviceResult && deviceResult.data?.status === 200) {
          const devices = Array.isArray(deviceResult.data.data) ? deviceResult.data.data : [];
          const allRecords = [];
          const BATCH_SIZE = 5;
          /* eslint-disable no-await-in-loop */
          for (let i = 0; i < devices.length; i += BATCH_SIZE) {
            const slice = devices.slice(i, i + BATCH_SIZE);
            await Promise.all(slice.map(async (d) => {
              const assetId = d?.oid;
              if (!assetId) return;
              try {
                const res = await APIGetMaintenanceRecordsByAssetIdQuiet(token, agencyID, assetId);
                const perAsset = parseMaintenanceRecordsFromApiResult(res);
                if (perAsset.length > 0) {
                  allRecords.push(...perAsset);
                }
              } catch (err) {
                // ignore individual failures
              }
            }));
          }
          /* eslint-enable no-await-in-loop */
          parsed = allRecords;
        }
      }

      setRecords(parsed);
    } catch (e) {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNavigtionClick(6);
    setNavigationState('insights');
    fetchAllRecords();
  }, []);

  useEffect(() => {
    if (!applyServicePicker) return;
    // Wait until both range ends are in state (presets can set apply before dates land).
    if (!Array.isArray(servicePickerDate) || servicePickerDate.length !== 2) return;

    const toMoment = (value) => {
      if (!value) return null;
      if (value instanceof DateObject) return moment(value.toDate());
      if (typeof value?.toDate === 'function') return moment(value.toDate());
      return moment(value);
    };

    const m0 = toMoment(servicePickerDate[0]);
    const m1 = toMoment(servicePickerDate[1]);
    if (m0?.isValid() && m1?.isValid()) {
      const from = m0.isSameOrBefore(m1) ? m0 : m1;
      const to = m0.isSameOrBefore(m1) ? m1 : m0;
      setServiceDateFrom(from.format('YYYY-MM-DD'));
      setServiceDateTo(to.format('YYYY-MM-DD'));
    }
    setApplyServicePicker(false);
  }, [applyServicePicker, servicePickerDate]);

  const onChangeSearchText = (e) => {
    setSearchText(e.target.value);
  };

  const resetServiceDate = () => {
    setServiceDateFrom('');
    setServiceDateTo('');
    setServicePickerDate([]);
    setServicePickerInputValue('Select Dates');
    setServiceDateRangePreset('');
  };

  const clearAdvancedFilters = () => {
    setSelectedAssetTypes([]);
    setSelectedAssetNames([]);
    resetServiceDate();
  };

  const assetNameOptions = useMemo(() => {
    const names = new Set();
    records.forEach((r) => {
      const n = maintenanceRecordDisplayAssetName(r);
      if (n && n !== '-') names.add(n);
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [records]);

  const activeAdvancedFilterCount = useMemo(() => {
    let n = selectedAssetTypes.length + selectedAssetNames.length;
    if (serviceDateFrom && serviceDateTo) n += 1;
    return n;
  }, [selectedAssetTypes, selectedAssetNames, serviceDateFrom, serviceDateTo]);

  const toggleAssetTypeFilter = (type) => {
    setSelectedAssetTypes((prev) => (
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    ));
  };

  const toggleAssetNameFilter = (name) => {
    setSelectedAssetNames((prev) => (
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    ));
  };

  const hasDisplayValue = (value) => {
    if (value === null || typeof value === 'undefined') return false;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  };

  const formatDisplayDate = (value) => {
    if (!hasDisplayValue(value)) return '';
    const parsed = moment(value);
    if (!parsed.isValid()) return String(value);
    return parsed.format('DD/MM/YYYY');
  };

  const formatDisplayNumber = (value) => {
    if (!hasDisplayValue(value)) return '';
    if (typeof value === 'number') return String(value);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(value) : String(value);
  };

  const buildReportHistorySections = (row) => {
    if (!row) return [];
    const rawRecord = row.rawRecord || {};
    const receiptInfo = rawRecord?.receiptInfo || {};
    const vendor = rawRecord?.vendor || {};
    const assetIdentification = rawRecord?.assetIdentification || {};
    const serviceDetails = rawRecord?.serviceDetails || {};
    const part = rawRecord?.partsAndConsumables || {};
    const cost = rawRecord?.costBreakdown || {};
    const scheduling = rawRecord?.schedulingAndWarranty || {};

    const sections = [
      {
        title: 'Receipt Information',
        rows: [
          { label: 'Invoice', value: receiptInfo?.receiptNumber },
          { label: 'Receipt Date', value: formatDisplayDate(receiptInfo?.receiptDate) },
          { label: 'Service Date', value: formatDisplayDate(receiptInfo?.serviceDate) },
          { label: 'Total Payment', value: receiptInfo?.currency },
          { label: 'Payment Method', value: receiptInfo?.paymentMethod },
        ],
      },
      {
        title: 'Vendor',
        rows: [
          { label: 'Workshop Name', value: vendor?.name },
          { label: 'Address', value: vendor?.address },
          { label: 'Contact Details', value: vendor?.contactNumber },
          { label: 'Email', value: vendor?.email },
        ],
      },
      {
        title: 'Asset Info',
        rows: [
          { label: 'Registration Number', value: assetIdentification?.vehicleRegNum || assetIdentification?.assetRegNum },
          { label: 'Odometer', value: formatDisplayNumber(assetIdentification?.odometerReading) },
          { label: 'Engine Hours', value: formatDisplayNumber(assetIdentification?.engineHours) },
          { label: 'Asset Type', value: maintenanceAssetTypeSectionDisplay(assetIdentification, rawRecord) },
        ],
      },
      {
        title: 'Service Details',
        rows: [
          { label: 'Service Type', value: serviceDetails?.type },
          { label: 'Description', value: serviceDetails?.jobDescription },
          { label: 'Labour Hours', value: formatDisplayNumber(serviceDetails?.labourHours) },
        ],
      },
      {
        title: 'Parts & Consumables',
        rows: [
          { label: 'Parts Summary', value: part?.summary },
        ],
      },
      {
        title: 'Cost',
        rows: [
          { label: 'Subtotal', value: formatDisplayNumber(cost?.subtotal) },
          { label: 'Tax', value: formatDisplayNumber(cost?.tax) },
          { label: 'Discount', value: formatDisplayNumber(cost?.discount) },
          { label: 'Total', value: formatDisplayNumber(cost?.totalAmount) },
          { label: 'Paid', value: formatDisplayNumber(cost?.paidAmount) },
          { label: 'Outstanding Balance', value: formatDisplayNumber(cost?.outstandingBalance) },
        ],
      },
      {
        title: 'Scheduling',
        rows: [
          { label: 'Next Service Date', value: formatDisplayDate(scheduling?.nextServiceDueDate) },
          { label: 'Next Service Mileage', value: formatDisplayNumber(scheduling?.nextServiceDueMileage) },
        ],
      },
    ];

    return sections
      .map((section) => ({
        ...section,
        rows: section.rows.filter((r) => hasDisplayValue(r.value)),
      }))
      .filter((section) => section.rows.length > 0);
  };

  const handleDownloadReportPdf = async (row) => {
    if (!row) return;
    setDownloadReportRow(row);
    await new Promise((resolve) => setTimeout(resolve, 0));

    const shouldUseModalRef = selectedReportRow && selectedReportRow.id === row.id;
    const targetRef = shouldUseModalRef ? reportPdfRef.current : downloadPdfRef.current;
    if (!targetRef) return;

    setExporting(true);
    setExportSnack(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      const canvas = await html2canvas(targetRef, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const safeName = String(row.invoiceNumber || row.assetName || 'maintenance-report')
        .replace(/[^a-zA-Z0-9-_ ]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .toLowerCase();
      pdf.save(`${safeName || 'maintenance-report'}.pdf`);
    } finally {
      setDownloadReportRow(null);
      setTimeout(() => {
        setExportSnack(false);
        setExporting(false);
      }, 600);
    }
  };

  const renderReportDetailCard = (row, refObj) => (
    <div className="maintenance-checklist-card" ref={refObj}>
      <h3 className="maintenance-checklist-title">
        {row?.rawRecord?.label
          || row?.rawRecord?.serviceDetails?.jobDescription
          || 'Maintenance report'}
      </h3>
      <div className="maintenance-checklist-info">
        <div className="maintenance-checklist-row">
          <span className="maintenance-checklist-label">Asset Name :</span>
          <span className="maintenance-checklist-value">
            {row.assetName}
          </span>
        </div>
        <div className="maintenance-checklist-row">
          <span className="maintenance-checklist-label">Asset Type :</span>
          <span className="maintenance-checklist-value">
            {row.assetType}
          </span>
        </div>
        <div className="maintenance-checklist-row">
          <span className="maintenance-checklist-label">Report Last Update :</span>
          <span className="maintenance-checklist-value">
            {row.serviceDate || row.receiptDate}
          </span>
        </div>
      </div>
      {buildReportHistorySections(row).map((section) => (
        <React.Fragment key={section.title}>
          <div className="maintenance-checklist-divider" />
          <h4 className="maintenance-form-section-title">{section.title}</h4>
          <div className="maintenance-checklist-items">
            {section.rows.map((item) => (
              <div
                className="maintenance-checklist-row"
                key={`${section.title}-${item.label}`}
              >
                <span className="maintenance-checklist-label">
                  {item.label}
                  {' :'}
                </span>
                <span className="maintenance-checklist-value">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const filteredRecords = useMemo(() => {
    const search = maintenanceNormalizeText(searchText);
    const hasServiceDateFilter = Boolean(serviceDateFrom && serviceDateTo);

    return records.filter((r) => {
      const receiptInfo = r?.receiptInfo || {};

      if (selectedAssetTypes.length > 0) {
        const typeTokens = maintenanceRecordAssetTypeTokens(r);
        const selectedLower = selectedAssetTypes.map((t) => maintenanceNormalizeText(t));
        const typeMatch = typeTokens.some((t) => selectedLower.includes(t));
        if (!typeMatch) return false;
      }

      if (selectedAssetNames.length > 0) {
        const displayName = maintenanceRecordDisplayAssetName(r);
        if (!selectedAssetNames.includes(displayName)) return false;
      }

      if (search && !maintenanceRecordSearchHaystack(r).includes(search)) {
        return false;
      }

      if (hasServiceDateFilter) {
        const serviceRaw = receiptInfo?.serviceDate
          ?? receiptInfo?.service_date
          ?? r?.serviceDate
          ?? r?.service_date;
        const svcMoment = serviceRaw ? moment(serviceRaw) : null;
        if (!svcMoment?.isValid()) return false;
        const fromMoment = moment(serviceDateFrom, 'YYYY-MM-DD', true).startOf('day');
        const toMoment = moment(serviceDateTo, 'YYYY-MM-DD', true).endOf('day');
        if (!fromMoment.isValid() || !toMoment.isValid()) return false;
        if (svcMoment.isBefore(fromMoment) || svcMoment.isAfter(toMoment)) {
          return false;
        }
      }

      return true;
    });
  }, [records, searchText, selectedAssetTypes, selectedAssetNames, serviceDateFrom, serviceDateTo]);

  const buildTableRows = () => filteredRecords.map((r, index) => {
    const assetIdentification = r?.assetIdentification || {};
    const receiptInfo = r?.receiptInfo || {};
    const vendor = r?.vendor || {};
    const assetName = maintenanceRecordDisplayAssetName(r);
    const assetType = maintenanceFormatAssetTypeDisplay(assetIdentification, r);
    const invoiceNumber = receiptInfo?.receiptNumber || '-';
    const receiptDate = receiptInfo?.receiptDate
      ? moment(receiptInfo.receiptDate).format('DD/MM/YYYY')
      : '-';
    const serviceRaw = receiptInfo?.serviceDate
      ?? receiptInfo?.service_date
      ?? r?.serviceDate
      ?? r?.service_date;
    const serviceMoment = serviceRaw ? moment(serviceRaw) : null;
    const serviceDateValid = Boolean(serviceMoment?.isValid());
    const serviceDateStr = serviceDateValid ? serviceMoment.format('DD/MM/YYYY') : '-';
    const serviceDateSort = serviceDateValid ? serviceMoment.valueOf() : 0;
    const workshopName = vendor?.name || '-';
    const description = r?.serviceDetails?.jobDescription || '-';
    const currency = r?.receiptInfo?.currency || '-';

    return {
      id: r?.id || r?.oid || index,
      assetName,
      assetType,
      invoiceNumber,
      receiptDate,
      serviceDate: serviceDateStr,
      serviceDateSort,
      workshopName,
      description,
      currency,
      rawRecord: r,
    };
  });

  const tableRows = buildTableRows();

  const columns = [
    {
      name: maintenanceInsightsTh('Asset Name'),
      selector: (row) => row.assetName,
      sortable: true,
      grow: 1.2,
      minWidth: '9rem',
      wrap: true,
    },
    {
      name: maintenanceInsightsTh('Invoice Number'),
      selector: (row) => row.invoiceNumber,
      sortable: true,
      grow: 1.2,
      minWidth: '9rem',
      wrap: true,
    },
    {
      name: maintenanceInsightsTh('Workshop Name'),
      selector: (row) => row.workshopName,
      sortable: true,
      grow: 1.4,
      minWidth: '9rem',
      wrap: true,
    },
    {
      name: maintenanceInsightsTh('Service Date'),
      selector: (row) => row.serviceDateSort,
      sortable: true,
      grow: 1,
      minWidth: '8rem',
      wrap: true,
      cell: (row) => row.serviceDate,
    },
    {
      name: maintenanceInsightsTh('Description'),
      selector: (row) => row.description,
      sortable: true,
      grow: 1.6,
      minWidth: '9rem',
      wrap: true,
    },
    {
      name: maintenanceInsightsTh('Total Payment'),
      selector: (row) => row.currency,
      sortable: true,
      grow: 1,
      minWidth: '8rem',
      wrap: true,
    },
    {
      name: '',
      allowOverflow: true,
      button: true,
      width: '48px',
      minWidth: '48px',
      grow: 0,
      ignoreRowClick: true,
      cell: (row, rowIndex) => (
        <MaintenanceInsightsRowMenu
          row={row}
          rowIndex={rowIndex}
          onView={setSelectedReportRow}
          onDownload={handleDownloadReportPdf}
        />
      ),
    },
  ];

  return (
    <div className="asset-performance-container maintenance-insights-container">
      <ListNavigationTab
        list={assetPerformanceNavigationList}
        navigtionClick={navigtionClick}
        setNavigtionClick={setNavigtionClick}
      />
      <div className="asset-performance-header">
        <Title
          title="Maintenance"
          subtitle="View all maintenance reports across assets and devices"
          titleSize="1.875rem"
          subtitleSize="1rem"
          lineHeight="2.375rem"
          subtitleLineHeight="1.5rem"
        />
      </div>
      <div className="asset-performance-body">
        <div className="maintenance-insights-card">
          <div className="maintenance-insights-card-filter">
            <div className="maintenance-insights-card-filter-inner">
              <SearchInput
                containerWidth="25rem"
                text="Search"
                value={searchText}
                onChangeText={onChangeSearchText}
              />
              <div className="maintenance-insights-filter-actions">
                <div
                  className="maintenance-insights-toolbar-service-dates"
                  title="Filter by service date (receiptInfo.serviceDate)"
                >
                  <div className="maintenance-insights-date-range-picker-wrap">
                    <DateRangePicker
                      date={servicePickerDate}
                      setDate={setServicePickerDate}
                      inputValue={servicePickerInputValue}
                      setInputValue={setServicePickerInputValue}
                      minDateRange={serviceMinDateRange}
                      setMinDateRange={setServiceMinDateRange}
                      maxDateRange={serviceMaxDateRange}
                      setMaxDateRange={setServiceMaxDateRange}
                      setApplyDatePicker={setApplyServicePicker}
                      dateRange={serviceDateRangePreset}
                      setDateRange={setServiceDateRangePreset}
                      displayFormat="DD/MM/YYYY"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="maintenance-insights-filters-trigger"
                  aria-expanded={Boolean(filterMenuAnchor)}
                  aria-haspopup="true"
                  onClick={(e) => setFilterMenuAnchor(filterMenuAnchor ? null : e.currentTarget)}
                >
                  <img loading="lazy" src={FilterIcon} alt="" className="maintenance-insights-filters-icon" />
                  <span>Filters</span>
                  {activeAdvancedFilterCount > 0 && (
                    <span className="maintenance-insights-filters-badge">{activeAdvancedFilterCount}</span>
                  )}
                </button>
                <Popover
                  open={Boolean(filterMenuAnchor)}
                  anchorEl={filterMenuAnchor}
                  onClose={() => setFilterMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ className: 'maintenance-insights-filter-popover-paper', elevation: 0 }}
                >
                  <div className="maintenance-insights-filter-popover">
                    <div className="maintenance-insights-filter-section">
                      <div className="maintenance-insights-filter-heading">Asset type</div>
                      <div className="maintenance-insights-filter-checkboxes">
                        {MAINTENANCE_ASSET_TYPE_FILTERS.map((type) => {
                          const tid = `maintenance-filter-atype-${type}`;
                          return (
                            <label key={type} htmlFor={tid} className="maintenance-insights-filter-check">
                              <span className="maintenance-insights-filter-check-label">{type}</span>
                              <Checkbox
                                width="16px"
                                height="16px"
                                borderRadius="4px"
                                value={tid}
                                text=""
                                checked={selectedAssetTypes.includes(type)}
                                onClick={() => toggleAssetTypeFilter(type)}
                                readOnly
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div className="maintenance-insights-filter-section">
                      <div className="maintenance-insights-filter-heading">Asset name</div>
                      <div className="maintenance-insights-filter-checkboxes maintenance-insights-filter-checkboxes-scroll">
                        {assetNameOptions.length === 0 ? (
                          <div className="maintenance-insights-filter-empty">No assets in current results</div>
                        ) : (
                          assetNameOptions.map((name, idx) => {
                            const nid = `maintenance-filter-aname-${idx}`;
                            return (
                              <label key={name} htmlFor={nid} className="maintenance-insights-filter-check">
                                <span className="maintenance-insights-filter-check-label">{name}</span>
                                <Checkbox
                                  width="16px"
                                  height="16px"
                                  borderRadius="4px"
                                  value={nid}
                                  text=""
                                  checked={selectedAssetNames.includes(name)}
                                  onClick={() => toggleAssetNameFilter(name)}
                                  readOnly
                                />
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="maintenance-insights-filter-popover-actions">
                      <button
                        type="button"
                        className="maintenance-insights-clear-filters"
                        onClick={() => clearAdvancedFilters()}
                        disabled={activeAdvancedFilterCount === 0}
                      >
                        Clear filters
                      </button>
                    </div>
                  </div>
                </Popover>
              </div>
            </div>
          </div>
          <div className="maintenance-insights-card-table">
            <Table
              tableData={tableRows}
              columns={columns}
              pagination
              paginationServer={false}
              module="maintenanceInsights"
              handlePageChange={() => {}}
              handlePerRowsChange={() => {}}
              loading={loading}
              totalRows={tableRows.length}
              backgroundColor="#F9FAFB"
              selectedRowCount={0}
              setSelectedRowCount={() => {}}
            />
            {!loading && tableRows.length === 0 && (
              <div className="maintenance-insights-empty">
                No maintenance reports found for the current filters.
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedReportRow && (
        <div className="maintenance-insights-modal-overlay">
          <div className="maintenance-insights-modal-panel">
            <div className="maintenance-insights-modal-topbar">
              <button
                type="button"
                className="maintenance-insights-modal-close"
                onClick={() => setSelectedReportRow(null)}
              >
                ×
              </button>
            </div>
            {renderReportDetailCard(selectedReportRow, reportPdfRef)}
          </div>
        </div>
      )}
      {downloadReportRow && (!selectedReportRow || selectedReportRow.id !== downloadReportRow.id) && (
        <div className="maintenance-insights-download-hidden">
          {renderReportDetailCard(downloadReportRow, downloadPdfRef)}
        </div>
      )}
      {exportSnack && (
        <div className="maintenance-export-notification-wrap">
          <SnackBar
            state={exportSnack}
            setState={setExportSnack}
            title={exporting ? 'Exporting Data' : 'Export triggered'}
            deleteSubtitle
            module="TelemetryLog"
            action="info"
          >
            <div className="maintenance-export-notification-body">
              Your download will begin shortly
            </div>
          </SnackBar>
        </div>
      )}
    </div>
  );
};

export default MaintenanceInsights;
